import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";
import InputPhoneSADC from "@/components/auth/InputPhoneSADC.jsx";
import InputCountrySADC from "@/components/fields/InputCountrySADC.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { resolveCurrentAddress } from "@/lib/addressLocation.js";
import {
  createRegionalHealthInstitution,
  loadMoHWorkspaceSetup,
  saveMoHWorkspaceSetup,
} from "@/pages/institutions/institutionService.js";

const CC_TO_COUNTRY = {
  "+244": "Angola", "+267": "Botswana", "+243": "DR Congo", "+268": "Eswatini",
  "+266": "Lesotho", "+265": "Malawi", "+258": "Mozambique", "+264": "Namibia",
  "+27": "South Africa", "+255": "Tanzania", "+260": "Zambia", "+263": "Zimbabwe",
  "+61": "Australia", "+1": "United States", "+49": "Germany", "+33": "France", "+64": "New Zealand",
  "+41": "Switzerland", "+971": "United Arab Emirates", "+44": "United Kingdom", "+1-CA": "Canada",
};

const GOV_DOMAINS = {
  Angola: "minsa.gov.ao", Botswana: "moh.gov.bw", "DR Congo": "sante.gouv.cd", Eswatini: "gov.sz",
  Lesotho: "gov.ls", Malawi: "health.gov.mw", Mozambique: "misau.gov.mz", Namibia: "mhss.gov.na",
  "South Africa": "health.gov.za", Tanzania: "moh.go.tz", Zambia: "moh.gov.zm", Zimbabwe: "mohcc.gov.zw",
};

const EMPTY_ADMIN = {
  name: "", email: "", phone: "", phoneCc: "+267", gov: "", dept: "", addr1: "", addr2: "", city: "", region: "", country: "",
};

function isGovEmailOk(country, email) {
  const domain = String(email || "").split("@")[1] || "";
  if (!country || !email) return false;
  if (country === "Tanzania") return /(^|\.)moh\.go\.tz$|(^|\.)afya\.go\.tz$/.test(domain);
  const rule = GOV_DOMAINS[country];
  if (!rule) return true;
  return new RegExp(`(^|\\.)${rule.replace(/\./g, "\\.")}$`).test(domain);
}

function toSetupProgress(values) {
  return {
    administrator: {
      name: String(values.name || "").trim(),
      email: String(values.email || "").trim().toLowerCase(),
      phone: String(values.phone || "").trim(),
      phoneCc: String(values.phoneCc || "").trim(),
      government: String(values.gov || "").trim(),
      department: String(values.dept || "").trim(),
      addressLine1: String(values.addr1 || "").trim(),
      addressLine2: String(values.addr2 || "").trim(),
      city: String(values.city || "").trim(),
      region: String(values.region || "").trim(),
      country: String(values.country || "").trim(),
    },
    // These are durable verification intents for the committed path only. They
    // deliberately do not send email or SMS; the delivery services remain
    // separately feature-gated until their providers are ready.
    verification: {
      email: {
        status: "not_requested",
        address: String(values.email || "").trim().toLowerCase(),
      },
      phone: {
        status: "not_requested",
        number: `${String(values.phoneCc || "").trim()}${String(values.phone || "").trim()}`,
      },
    },
    intake: { roster: "not_started", facilities: "not_started", members: "not_started" },
  };
}

function toExploreProgress() {
  return {
    mode: "explore",
    onboarding: { journey: "moh", step: "explore" },
    intake: { roster: "not_started", facilities: "not_started", members: "not_started" },
  };
}

function fromSetupProgress(progress) {
  const admin = progress?.administrator || {};
  return {
    name: admin.name || "", email: admin.email || "", phone: admin.phone || "", phoneCc: admin.phoneCc || "+267",
    gov: admin.government || "", dept: admin.department || "", addr1: admin.addressLine1 || "", addr2: admin.addressLine2 || "",
    city: admin.city || "", region: admin.region || "", country: admin.country || "",
  };
}

function makeRequestKey() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function MoHOnboarding({ backToCategory, resumeMode = false }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetInstitutionId = String(searchParams.get("institution") || "").trim();
  const requestKeyRef = useRef(makeRequestKey());
  const locationRequestRef = useRef({ id: 0, controller: null });
  const [admin, setAdmin] = useState(EMPTY_ADMIN);
  const [loading, setLoading] = useState(Boolean(resumeMode));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [locationBusy, setLocationBusy] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [storedSetupProgress, setStoredSetupProgress] = useState({});

  useEffect(() => () => locationRequestRef.current.controller?.abort(), []);

  useEffect(() => {
    if (!resumeMode) return undefined;
    if (!targetInstitutionId) {
      setLoading(false);
      setError(t("mohOnboarding.resumeMissingWorkspace", "No authorised MoH workspace was selected."));
      return undefined;
    }
    let active = true;
    loadMoHWorkspaceSetup({ institutionId: targetInstitutionId })
      .then((setup) => {
        if (!active) return;
        setAdmin(fromSetupProgress(setup?.setupProgress));
        setStoredSetupProgress(setup?.setupProgress || {});
      })
      .catch(() => {
        if (active) setError(t("mohOnboarding.resumeDenied", "This MoH setup is not available for the active authorised Institution."));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [resumeMode, t, targetInstitutionId]);

  const updateAdmin = (patch) => setAdmin((current) => ({ ...current, ...patch }));
  const emailOk = isGovEmailOk(admin.country, admin.email);
  const canContinue = Boolean(
    admin.name.trim() && admin.email.trim() && admin.phone.trim() && emailOk && admin.gov.trim() && admin.dept.trim() && admin.country.trim(),
  );

  const returnToHome = (institutionId, { fromExplore = false } = {}) => {
    navigate(`/institutions/home?institution=${encodeURIComponent(institutionId)}`, {
      replace: true,
      state: fromExplore ? { mohExploreArrival: true } : undefined,
    });
  };

  const useCurrentLocation = async () => {
    locationRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const requestId = locationRequestRef.current.id + 1;
    locationRequestRef.current = { id: requestId, controller };
    setLocationError("");
    setLocationBusy(true);
    try {
      const { address } = await resolveCurrentAddress({ signal: controller.signal });
      if (locationRequestRef.current.id !== requestId) return;
      setAdmin((current) => ({
        ...current,
        addr1: address.streetAddress || current.addr1,
        city: address.city || current.city,
        region: address.region || current.region,
        country: address.country || current.country,
      }));
    } catch {
      if (locationRequestRef.current.id !== requestId || controller.signal.aborted) return;
      setLocationError(t("mohOnboarding.locationLookupFailed", "We couldn’t find your address. Enter it manually or try again."));
    } finally {
      if (locationRequestRef.current.id === requestId) setLocationBusy(false);
    }
  };

  const saveCommittedSetup = async () => {
    if (saving) return;
    if (!canContinue) {
      setError(t("mohOnboarding.completeValidation", "Enter the required administrator and government details before completing setup."));
      return;
    }
    setSaving(true);
    setError("");
    const setupProgress = toSetupProgress(admin);
    try {
      if (resumeMode) {
        await saveMoHWorkspaceSetup({
          institutionId: targetInstitutionId,
          setupProgress,
          status: "complete_later",
        });
        returnToHome(targetInstitutionId);
        return;
      }
      const result = await createRegionalHealthInstitution({
        name: admin.gov || admin.dept,
        country: admin.country,
        email: admin.email,
        phone: `${admin.phoneCc || ""}${admin.phone || ""}`,
        idempotencyKey: requestKeyRef.current,
        setupProgress,
      });
      returnToHome(result?.institutionId || "");
    } catch (nextError) {
      setError(nextError?.message || t("mohOnboarding.saveError", "The authorised MoH workspace could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const exploreFirst = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      if (resumeMode) {
        await saveMoHWorkspaceSetup({
          institutionId: targetInstitutionId,
          setupProgress: Object.keys(storedSetupProgress).length ? storedSetupProgress : toExploreProgress(),
          status: "explore",
        });
        returnToHome(targetInstitutionId, { fromExplore: true });
        return;
      }
      const result = await createRegionalHealthInstitution({
        name: t("mohOnboarding.explorationWorkspaceName", "AfriMed™ MoH exploration workspace"),
        country: "",
        email: "",
        phone: "",
        idempotencyKey: requestKeyRef.current,
        setupProgress: toExploreProgress(),
      });
      returnToHome(result?.institutionId || "", { fromExplore: true });
    } catch (nextError) {
      setError(nextError?.message || t("mohOnboarding.exploreError", "The authorised MoH exploration workspace could not be opened."));
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setStepIndex((current) => current - 1);
      return;
    }
    if (resumeMode) returnToHome(targetInstitutionId);
    else backToCategory();
  };

  if (loading) return <RouteFallback label={t("mohOnboarding.loading", "Loading authorised MoH setup…")} />;

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar
        step={stepIndex + 1}
        total={3}
        onBack={goBack}
      />
      <OnboardingSection
        header={<div><h2 className="text-title-large text-on-surface">{stepIndex === 0 ? t("mohOnboarding.title", "Administrator details") : stepIndex === 1 ? t("mohOnboarding.workspaceTitle", "Government and workspace details") : t("mohOnboarding.reviewTitle", "Explore before commitment")}</h2><p className="mt-2 text-body-medium text-on-surface-var">{stepIndex === 2 ? t("mohOnboarding.reviewIntro", "You can explore the authorised Institution workspace without saving administrator, government, facility, roster, Clinician, Member, Patient, or CSV data.") : t("mohOnboarding.intro", "Create an authorised Institution workspace to explore AfriMed™. Facility and roster intake can be completed later.")}</p></div>}
        footer={<div className="grid gap-2">{stepIndex < 2 ? <Button fullWidth disabled={saving} onClick={() => setStepIndex((current) => current + 1)}>{t("mohOnboarding.continueExploring", "Continue exploring")}</Button> : <><Button fullWidth disabled={saving} onClick={() => void exploreFirst()}>{saving ? t("mohOnboarding.saving", "Saving…") : t("mohOnboarding.exploreFirst", "Explore AfriMed™ first")}</Button><Button fullWidth variant="tonal" disabled={saving} onClick={() => void saveCommittedSetup()}>{t("mohOnboarding.completeSetup", "Complete setup")}</Button></>}</div>}
      >
        <div className="grid gap-3">
          {error ? <p className="rounded-[12px] bg-[var(--state-critical-08)] px-3 py-2 text-body-small text-[rgb(var(--schemes-critical))]" role="alert">{error}</p> : null}
          {stepIndex === 0 ? <><TextField56 label={t("mohOnboarding.fullName", "Full name*")} value={admin.name} onChange={(value) => updateAdmin({ name: value })} />
          <TextField56 label={t("mohOnboarding.email", "Email*")} value={admin.email} onChange={(value) => updateAdmin({ email: value })} />
          {!emailOk && admin.email ? <p className="rounded-[12px] bg-[var(--state-critical-08)] px-3 py-2 text-body-small text-[rgb(var(--schemes-critical))]">{t("mohOnboarding.emailValidation", "Use the official government email for the selected country.")}</p> : null}
          <p className="text-body-small text-on-surface-var" data-testid="moh-verification-readiness">{t("mohOnboarding.verificationReadiness", "Email and phone verification are prepared for committed setup. Exploring does not send verification messages.")}</p>
          <div>
            <div className="mb-1 text-label-medium text-on-surface-var">{t("mohOnboarding.phone", "Phone*")}</div>
            <InputPhoneSADC value={admin.phone} cc={admin.phoneCc} onChange={(phone) => updateAdmin({ phone })} onChangeCc={(phoneCc) => updateAdmin({ phoneCc, country: CC_TO_COUNTRY[phoneCc] || admin.country })} />
          </div></> : null}
          {stepIndex === 1 ? <><TextField56 label={t("mohOnboarding.government", "Government*")} value={admin.gov} onChange={(value) => updateAdmin({ gov: value })} placeholder={t("mohOnboarding.governmentPlaceholder", "For example: Government of Botswana")} />
          <TextField56 label={t("mohOnboarding.department", "Ministry / Department*")} value={admin.dept} onChange={(value) => updateAdmin({ dept: value })} placeholder={t("mohOnboarding.departmentPlaceholder", "For example: Ministry of Health")} />
          <Button variant="link" size="default" className="w-fit gap-2 no-underline hover:no-underline" disabled={locationBusy} onClick={() => void useCurrentLocation()}><img src="/icons/doctor/near_me.svg" alt="" className="size-[18px]" />{locationBusy ? t("mohOnboarding.locating", "Locating…") : t("mohOnboarding.useCurrentLocation", "Use my current location")}</Button>
          {locationError ? <p className="rounded-[12px] bg-[var(--state-critical-08)] px-3 py-2 text-body-small text-[rgb(var(--schemes-critical))]" role="alert">{locationError}</p> : null}
          <TextField56 label={t("mohOnboarding.address1", "Address line 1")} value={admin.addr1} onChange={(value) => updateAdmin({ addr1: value })} />
          <TextField56 label={t("mohOnboarding.address2", "Address line 2")} value={admin.addr2} onChange={(value) => updateAdmin({ addr2: value })} />
          <div className="grid grid-cols-2 gap-3"><TextField56 label={t("mohOnboarding.city", "City")} value={admin.city} onChange={(value) => updateAdmin({ city: value })} /><TextField56 label={t("mohOnboarding.region", "Region / State")} value={admin.region} onChange={(value) => updateAdmin({ region: value })} /></div>
          <InputCountrySADC label={t("mohOnboarding.country", "Country*")} value={admin.country} onChange={(country) => updateAdmin({ country })} /></> : null}
          {stepIndex === 2 ? <SettingsCard fullWidth className="p-4 text-body-small text-on-surface-var"><strong className="block text-title-medium text-on-surface">{t("mohOnboarding.intakeTitle", "Complete the roster later")}</strong><p className="mt-2 mb-0">{t("mohOnboarding.intakeBody", "Facility, operational Member and Clinician intake is optional at this stage. It will use a governed preview-and-commit workflow; no CSV is uploaded from this screen.")}</p></SettingsCard> : null}
        </div>
      </OnboardingSection>
    </div>
  );
}
